import React, { useState, useEffect } from 'react';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { withLocalePrefix, tList } from 'shared/utils/translation';
import Form from '@grnhse/seedling/lib/birch/components/forms/form';
import SingleSelectField from '@grnhse/seedling/lib/birch/components/forms/fields/single_select_field';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import {
  PrimaryButton,
  SecondaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import TextAreaField from '@grnhse/seedling/lib/birch/components/forms/fields/text_area_field';
import { standardRequestWithErrorFlash } from 'shared/utils/request';
import RadioGroup from '@grnhse/seedling/lib/birch/components/radio_group';
import TextInput from '@grnhse/seedling/lib/birch/components/forms/text_input';
import {
  ApplicationScore,
  MatchStrength,
  OverrideMatchScoreFormData,
  TalentMatchingApplication,
} from 'talent_matching/types';

const t = withLocalePrefix('talent_matching.modals.override_match_score');

type Props = {
  isOpen: boolean;
  onClose: () => void;
  application: TalentMatchingApplication;
  updatePerson: (person: TalentMatchingApplication) => void;
};

const matchScoreOptions = [
  { value: 'unknown', label: t('labels.unknown') },
  { value: 'limited_match', label: t('labels.limited_match') },
  { value: 'partial_match', label: t('labels.partial_match') },
  { value: 'good_match', label: t('labels.good_match') },
  { value: 'strong_match', label: t('labels.strong_match') },
];

const OverrideMatchScoreModal = ({
  isOpen,
  onClose,
  application,
  updatePerson,
}: Props) => {
  // RadioGroup component does not play well with seedling field components
  // will need to be updated later
  const [reason, setReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const enOverrideReasons = tList(
    'talent_matching.modals.override_match_score.cause_options',
    undefined,
    true,
    'en'
  );

  useEffect(() => {
    if (isOpen) {
      setReason(application?.overrideReason || '');
    }
  }, [application, isOpen]);

  const onSubmit = async (data: OverrideMatchScoreFormData) => {
    const override: Partial<ApplicationScore> = {
      overrideReason: reason === t('labels.other') ? otherReason : reason,
      overrideMatchStrength: data?.match_strength
        ?.value as MatchStrength | null,
      overrideNote: data?.note,
    };

    try {
      await standardRequestWithErrorFlash.post(
        application.overrideMatchScorePath,
        {
          match_strength: override.overrideMatchStrength,
          note: override.overrideNote,
          reason: override.overrideReason,
        }
      );

      updatePerson({
        ...application,
        ...override,
        matchStrength: override.overrideMatchStrength,
      } as TalentMatchingApplication);

      handleClose();
    } catch (error) {
      return;
    }
  };

  const handleClose = () => {
    setOtherReason('');
    setReason('');
    onClose();
  };

  const overrideReasonOptions = enOverrideReasons.reduce((acc, cause) => {
    if (cause === t('labels.other')) {
      acc.push({
        label: (
          <Stack sx={{ paddingBottom: 1 }}>
            {`${t('labels.other')}:`}
            <TextInput
              label=""
              disabled={reason !== cause}
              onChange={(e) => setOtherReason(e.target.value)}
            />
          </Stack>
        ),
        value: cause,
      });
    } else {
      acc.push({
        label: cause,
        value: cause,
      });
    }
    return acc;
  }, [] as Array<{ label: string | React.ReactNode; value: string }>);

  const initialMatchStrength =
    application?.overrideMatchStrength || application?.matchStrength;

  const initialReason = application?.overrideReason;

  const defaultValues = {
    match_strength: initialMatchStrength
      ? matchScoreOptions.find(
          (option) => option.value === initialMatchStrength
        )
      : null,
    note: application?.overrideNote,
    reason: initialReason,
  };

  return (
    <DataEntryModal
      size="medium"
      isOpen={isOpen}
      onDismiss={handleClose}
      ariaLabel={'override-match-score-modal'}
    >
      <DataEntryModal.Header onClick={handleClose}>
        <span className="pii-blur">
          {t(application.matchStrength ? 'title' : 'assign_title', {
            name: application.fullName,
          })}
        </span>
      </DataEntryModal.Header>
      <DataEntryModal.Body>
        <Form<OverrideMatchScoreFormData>
          onSubmit={onSubmit}
          defaultValues={defaultValues}
        >
          <Stack spacing={2}>
            <SingleSelectField
              name={'match_strength'}
              label={t('labels.match_score')}
              items={matchScoreOptions}
              clearable={false}
            />
            <TextAreaField name={'note'} label={t('labels.note')} />

            <RadioGroup
              legend={t('labels.cause')}
              value={reason}
              onChange={(selected) => setReason(selected as string)}
              // @ts-expect-error TS(2322) FIXME: Type 'Option[]' is not assignable to type 'Option[]'.
              options={overrideReasonOptions}
            />

            <DataEntryModal.Footer>
              <Grid container spacing={1}>
                <Grid>
                  <SecondaryButton onClick={handleClose}>
                    {t('actions.cancel')}
                  </SecondaryButton>
                </Grid>
                <Grid>
                  <PrimaryButton type="submit">
                    {t('actions.save')}
                  </PrimaryButton>
                </Grid>
              </Grid>
            </DataEntryModal.Footer>
          </Stack>
        </Form>
      </DataEntryModal.Body>
    </DataEntryModal>
  );
};

export default OverrideMatchScoreModal;
